import React from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { BaseGatewayImage, DANHMUCPHANMEM_API, ROLE_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { acceptImage, messageError, messageToast, status200, styleSelectDefault, styleSelectError } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Editor from '@app/components/editor/Editor';
import InputFile from '@app/components/input-file/InputFile';
import { AsyncPaginate } from 'react-select-async-paginate';

const schema = yup.object().shape({
    maPhanMem: yup.string().required(messageError.emptyMessage).max(50, 'Mã phần mềm không được vượt quá 50 ký tự'),
    tenPhanMem: yup.string().required(messageError.emptyMessage).max(255, 'Tên phần mềm không được vượt quá 255 ký tự')
});

const DanhMucPhanMemEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['danhMucPhanMem', itemId], () => Get(DANHMUCPHANMEM_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const editMutation = useMutation((value) => Put(DANHMUCPHANMEM_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const model = { ...values };

        model.lstAllowedRoles = model.lstAllowedRoles.map((x) => {
            return {
                id: x.value,
                name: x.label
            };
        });

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(['danhMucPhanMem', itemId], model);
                    history.push(URI_INTERNAL.DANHMUCPHANMEM_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    const listRole = useQuery(['getAllRolesFromAuthServer'], () =>
        Get(ROLE_API.GET_PAGING, null, {
            PageIndex: 1,
            PageSize: 10000
        })
    );

    const lstRole =
        listRole &&
            listRole.data &&
            listRole.data.data &&
            listRole.data.data.length > 0
            ? listRole.data.data.map((x) => ({
                label: x.name,
                value: x.id
            }))
            : [];

    const loadOptions = async (inputValue, loadedOptions, { page }) => {
        const filtered = lstRole
            .filter((o) =>
                o.label.toLowerCase().includes(inputValue.toLowerCase())
            )
            .slice(0, 6);
        return {
            options: filtered,
            hasMore: false,
            additional: { page: page + 1 }
        };
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy phần mềm</div>;
    }

    const currentData = itemQuery.data;

    return (
        <Card>
            <Card.Header as="h5">Sửa phần mềm</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        maPhanMem: currentData.maPhanMem || currentData.MaPhanMem || '',
                        tenPhanMem: currentData.tenPhanMem || currentData.TenPhanMem || '',
                        moTa: currentData.moTa || currentData.MoTa || '',
                        lstAllowedRoles:
                            currentData.lstAllowedRoles &&
                                currentData.lstAllowedRoles
                                ? currentData.lstAllowedRoles.map(
                                    (x) => ({
                                        label: x.name,
                                        value: x.id
                                    })
                                )
                                : [],
                        imagePath: currentData.imagePath
                            ? currentData.imagePath
                            : "",
                        orderNumber:
                                currentData.orderNumber
                                    ? currentData.orderNumber
                                    : 0,
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Mã phần mềm',
                                        name: 'maPhanMem',
                                        value: values.maPhanMem,
                                        onChange: handleChange,
                                        isInvalid: errors.maPhanMem,
                                        error: errors.maPhanMem
                                    },
                                    {
                                        label: 'Tên phần mềm',
                                        name: 'tenPhanMem',
                                        value: values.tenPhanMem,
                                        onChange: handleChange,
                                        isInvalid: errors.tenPhanMem,
                                        error: errors.tenPhanMem
                                    },
                                ]}
                            />
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Quyền được cho phép truy cập
                                </Form.Label>
                                <Col sm="10">
                                    <AsyncPaginate
                                        isMulti
                                        name="lstAllowedRoles"
                                        placeholder="Chọn quyền được cho phép truy cập"
                                        value={values.lstAllowedRoles}
                                        loadOptions={loadOptions} // your existing loadOptions
                                        styles={
                                            errors.lstAllowedRoles
                                                ? styleSelectError
                                                : styleSelectDefault
                                        }
                                        onChange={(options) => {
                                            setFieldValue(
                                                'lstAllowedRoles',
                                                options
                                            );
                                        }}
                                        additional={{
                                            page: 1
                                        }}
                                    />
                                    <div
                                        className="invalid-feedback"
                                        style={{ display: 'block' }}
                                    >
                                        {errors.lstAllowedRoles}
                                    </div>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Ảnh đại diện
                                </Form.Label>
                                <Col sm="10">
                                    <InputFile
                                        enableReinitialize={true}
                                        values={
                                            values.imagePath
                                                ? [
                                                    isValidUrl(
                                                        values.imagePath
                                                    )
                                                        ? values.imagePath
                                                        : BaseGatewayImage +
                                                        values.imagePath
                                                ]
                                                : []
                                        }
                                        name="imagePath"
                                        accept={acceptImage}
                                        onChange={(files) => {
                                            if (files.length > 0) {
                                                setFieldValue(
                                                    'imagePath',
                                                    files[0].filePath,
                                                    true
                                                );
                                                setFieldValue(
                                                    'imageThumbnail',
                                                    files[0].thumbPath,
                                                    true
                                                );
                                            } else {
                                                setFieldValue(
                                                    'imagePath',
                                                    '',
                                                    true
                                                );
                                                setFieldValue(
                                                    'imageThumbnail',
                                                    '',
                                                    true
                                                );
                                            }
                                        }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Sắp xếp
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="number"
                                        name="orderNumber"
                                        placeholder="Sắp xếp"
                                        value={values.orderNumber}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Mô tả
                                </Form.Label>
                                <Col sm="10">
                                    <Col sm="10">
                                        <Editor
                                            name="moTa"
                                            value={values.moTa}
                                            onChange={(value) => {
                                                setFieldValue(
                                                    'moTa',
                                                    value
                                                );
                                            }}
                                        />
                                    </Col>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">
                                            Cập nhật
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.DANHMUCPHANMEM_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default DanhMucPhanMemEdit;
